import {type ForecastState, type Harvest, type HarvestRecommendation} from 'client';
import {
  dateReader,
  gramsToKilos,
  kilosToGrams,
  kilosToTons,
  roundTwoDecimals,
  tonsToKilos,
} from '../../../helpers/UnitsFormatHandler';

export const FULL_HARVEST_PERCENTAGE = 0.98;

export function reducer(state: IHarvest, action: IHarvestAction): IHarvest {
  switch (action.type) {
    case actionTypeEnum.setProperties:
      return {...state, ...action};
    case actionTypeEnum.setBiomass:
      if (action.biomass && state.forecast && state.focusedField === 'biomass') {
        const count = tonsToKilos(action.biomass) / state.forecast.closingWeight;
        const fullHarvest = action.biomass >= kilosToTons(state.forecast.closingBiomass) * FULL_HARVEST_PERCENTAGE;
        const newState: IHarvest = {...state, biomass: action.biomass, count, fullHarvest, typingField: 'biomass'};
        const availabilityBiomass = state.isSequence ? action.biomass * state.datesSequence.length : action.biomass;
        const availabilityCount = state.isSequence ? count * state.datesSequence.length : count;
        const availability = availabilityHandler(
          {...newState, count: availabilityCount, biomass: availabilityBiomass},
          state.forecast,
        );
        return {...newState, availability};
      } else {
        return state;
      }
    case actionTypeEnum.setCount:
      if (action.count && state.forecast && state.focusedField === 'count') {
        const biomass = roundTwoDecimals(kilosToTons(action.count * state.forecast.closingWeight));
        const fullHarvest = action.count >= state.forecast.closingCount * FULL_HARVEST_PERCENTAGE;
        const newState: IHarvest = {...state, count: action.count, biomass, fullHarvest, typingField: 'count'};
        const availabilityBiomass = state.isSequence ? biomass * state.datesSequence.length : biomass;
        const availabilityCount = state.isSequence ? action.count * state.datesSequence.length : action.count;
        const availability = availabilityHandler(
          {...newState, count: availabilityCount, biomass: availabilityBiomass},
          state.forecast,
        );
        return {...newState, availability};
      } else {
        return state;
      }
    case actionTypeEnum.setWeight: {
      const weight = action.weight;
      if (weight) {
        let count = state.count;
        let biomass = state.biomass;

        if (state.typingField === 'biomass') {
          count = tonsToKilos(biomass) / gramsToKilos(weight);
        }
        if (state.typingField === 'count') {
          biomass = roundTwoDecimals(kilosToTons(count * gramsToKilos(weight)));
        }
        const newState = {...state, weight, count, biomass, isGraded: true};
        const availability = state.forecast ? availabilityHandler(newState, state.forecast) : undefined;
        return {...newState, availability};
      } else {
        return state;
      }
    }
    case actionTypeEnum.setForecast: {
      if (action.forecast) {
        const forecast = forecastHandler(action?.forecast, state);
        const availability = forecast ? availabilityHandler(state, forecast) : undefined;
        const biomass = state.fullHarvest ? roundTwoDecimals(kilosToTons(forecast?.closingBiomass)) : state.biomass;
        const count = state.fullHarvest ? forecast?.closingCount : state.count;
        return {...state, forecast, availability, biomass, count};
      } else {
        return state;
      }
    }
    case actionTypeEnum.setHarvest:
      if (action.harvestToEdit) {
        const biomass = kilosToTons(action.harvestToEdit.biomass);
        const weight = action.harvestToEdit.isGraded ? kilosToGrams(action.harvestToEdit.weight) : undefined;
        const date = dateReader(action.harvestToEdit.date);
        const newState: IHarvest = {
          ...state,
          ...action.harvestToEdit,
          biomass,
          weight,
          date,
          originalDate: date,
          allocatedBiomass: action.harvestToEdit.biomass,
          allocatedCount: action.harvestToEdit.count,
        };
        const forecast = state?.forecast ? forecastHandler(state?.forecast, newState) : undefined;
        const availability = forecast ? availabilityHandler(newState, forecast) : undefined;
        return {...newState, forecast, availability};
      } else {
        return state;
      }
    case actionTypeEnum.setRecommendation:
      if (action.harvestRecommendation) {
        const newState = {
          ...state,
          count: Math.round(action.harvestRecommendation.count),
          biomass: kilosToTons(action.harvestRecommendation.biomass),
          date: dateReader(action.harvestRecommendation.date),
          cageId: action.harvestRecommendation.cageId,
          fullHarvest: action.harvestRecommendation.fullHarvest,
          siteId: action.harvestRecommendation.siteId,
        };
        const availability = state.forecast ? availabilityHandler(newState, state.forecast) : undefined;
        return {...newState, availability};
      } else {
        return state;
      }
    case actionTypeEnum.setFullHarvest: {
      const newState = {...state, ...action};
      const fullHarvest = action.fullHarvest ?? false;
      return setFull(newState, fullHarvest);
    }
    case actionTypeEnum.setHarvestPlant: {
      return {...state, harvestPlantId: action.harvestPlantId};
    }
    case actionTypeEnum.setHarvestSequence: {
      if (action.isSequence !== undefined) {
        const datesSequence = action.isSequence && state.date !== null ? [state.date] : [];
        return {...state, isSequence: action.isSequence, datesSequence};
      }
      return state;
    }
    case actionTypeEnum.setHarvestSequenceDates: {
      if (state.forecast && action.datesSequence && state.isSequence) {
        const totalBiomass = state.biomass * action.datesSequence.length;
        const totalCount = state.biomass * action.datesSequence.length;
        const newState = {...state, datesSequence: action.datesSequence};
        const availability = availabilityHandler(
          {...newState, biomass: totalBiomass, count: totalCount},
          state.forecast,
        );
        const biomass = kilosToTons(state.forecast.closingBiomass) / action.datesSequence.length;
        return {...newState, biomass, availability};
      }
      return state;
    }
    default:
      console.error('Harvest menu reducer: Unknown action.');
      return state;
  }
}

export interface IAvailability {
  closingBiomass: number;
  closingCount: number;
  closingWeight: number;
}

export interface IHarvest {
  id?: string;
  cageId?: string;
  siteId?: string;
  biomass: number;
  allocatedBiomass: number;
  allocatedCount: number;
  count: number;
  weight?: number;
  date: Date | null;
  originalDate: Date;
  harvestPlantId?: string | null;
  fullHarvest: boolean;
  isGraded: boolean;
  isSequence: boolean;
  datesSequence: Date[];
  isEmergency?: boolean;
  forecast?: ForecastState;
  availability?: IAvailability;
  focusedField?: 'biomass' | 'count' | 'weight';
  typingField?: 'biomass' | 'count';
}

export enum actionTypeEnum {
  setBiomass = 'setBiomass',
  setCount = 'setCount',
  setWeight = 'setWeight',
  setForecast = 'setForecast',
  setHarvestPlant = 'setHarvestPlant',
  setRecommendation = 'setRecommendation',
  setHarvest = 'setHarvest',
  setProperties = 'setProperties',
  setFullHarvest = 'setFullHarvest',
  setHarvestSequence = 'setHarvestSequence',
  setHarvestSequenceDates = 'setHarvestSequenceDates',
}

export interface IHarvestAction extends Partial<IHarvest> {
  type: actionTypeEnum;
  harvestToEdit?: Harvest;
  harvestRecommendation?: HarvestRecommendation;
}

const availabilityHandler = (
  state: IHarvest,
  forecast: ForecastState,
): {closingBiomass: number; closingCount: number; closingWeight: number} => {
  if (state.fullHarvest) {
    return {
      closingBiomass: 0,
      closingCount: 0,
      closingWeight: 0,
    };
  }
  const leastAmountOfHarvestableCount = 10;
  const _closingBiomass = forecast.closingBiomass - tonsToKilos(state.biomass || 0);
  const closingBiomass = _closingBiomass > 0 ? _closingBiomass : 0;
  const _closingCount = forecast.closingCount - (state?.count || 0);
  const closingCount = _closingCount > leastAmountOfHarvestableCount ? _closingCount : 0;
  const closingWeight = closingCount > 0 ? closingBiomass / closingCount : 0;
  return {
    closingBiomass: kilosToTons(closingBiomass),
    closingCount,
    closingWeight: kilosToGrams(closingWeight),
  };
};

const setFull = (state: IHarvest, fullHarvest: boolean): IHarvest => {
  if (fullHarvest && state.forecast) {
    const biomass = roundTwoDecimals(kilosToTons(state.forecast?.closingBiomass));
    const count = state.forecast?.closingCount;
    const availability = state.forecast ? availabilityHandler(state, state.forecast) : undefined;
    return {...state, biomass, count, availability};
  }

  return state;
};

const forecastHandler = (forecast: ForecastState, editHarvest?: IHarvest): ForecastState => {
  const forecastDate = dateReader(forecast.date);

  if (editHarvest?.id && editHarvest && forecastDate.getTime() > editHarvest.originalDate.getTime()) {
    const availableCount = forecast.availableCount + editHarvest.allocatedCount;
    const availableBiomass = forecast.availableBiomass + editHarvest.allocatedBiomass;
    const availableWeight = availableBiomass / availableCount;
    const closingCount = forecast.closingCount + editHarvest.allocatedCount;
    const closingBiomass = forecast.closingBiomass + editHarvest.allocatedBiomass;
    const closingWeight = closingBiomass / closingCount;
    return {
      ...forecast,
      availableCount,
      availableBiomass,
      availableWeight,
      closingCount,
      closingBiomass,
      closingWeight,
    };
  } else if (editHarvest?.id && editHarvest && forecastDate.getTime() === editHarvest.originalDate.getTime()) {
    const availableCount = forecast.availableCount;
    const availableBiomass = forecast.availableBiomass;
    const availableWeight = availableBiomass / availableCount;
    const closingCount = forecast.closingCount + editHarvest.allocatedCount;
    const closingBiomass = forecast.closingBiomass + editHarvest.allocatedBiomass;
    const closingWeight = closingBiomass / closingCount;
    return {
      ...forecast,
      availableCount,
      availableBiomass,
      availableWeight,
      closingCount,
      closingBiomass,
      closingWeight,
    };
  } else {
    return forecast;
  }
};
